import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AnalyticsDashboardSelector from 'insights/analytics-dashboards/AnalyticsDashboardSelector';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {assertNever} from 'util/Types';

import {
  DashboardAndBlockSelectorFormGroupQuery,
  DashboardAndBlockSelectorFormGroupQueryVariables,
} from './__generated/DashboardAndBlockSelectorFormGroupQuery';
import BlockInDashboardSelector from './BlockInDashboardSelector';

type AnalyticsDashboard = ExtractByTypename<
  DashboardAndBlockSelectorFormGroupQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

function DashboardAndBlockSelectorSkeleton(props: {
  dashboardSelector: JSX.Element;
  blockSelector: JSX.Element;
}): JSX.Element {
  return (
    <>
      <FormGroup>
        <FieldLabel label="Dashboard" />
        {props.dashboardSelector}
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Block" />
        {props.blockSelector}
      </FormGroup>
    </>
  );
}

export const DASHBOARD_AND_BLOCK_SELECTOR_FORM_GROUP_QUERY = gql`
  query DashboardAndBlockSelectorFormGroupQuery($analyticsDashboardId: StrictString!)
  @skipAudit
  @engComponent(name: INSIGHTS) {
    analyticsDashboard(id: $analyticsDashboardId) {
      ... on AnalyticsDashboardNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Adapter, helper component between DashboardAndBlockSelectorFormGroup and AnalyticsDashboardSelector:
 *   - The parent DashboardAndBlockSelectorFormGroup component has dashboard ID only
 *   - The child AnalyticsDashboardSelector component expects dashboard ID *and* name
 *
 * This component adds the name by using the GraphQL query result.
 */
function DashboardSelectorAdapter(props: {
  selectedDashboardId: string | null;
  selectedDashboardQueryResult: SkippableBenchlingQueryResult<
    DashboardAndBlockSelectorFormGroupQuery,
    DashboardAndBlockSelectorFormGroupQueryVariables
  >;
  onSelect: (dashboardId: string) => void;
}): JSX.Element | null {
  const sharedProps = {
    onSelect: (newDashboard: AnalyticsDashboard) => {
      props.onSelect(newDashboard.id);
    },
  };
  switch (props.selectedDashboardQueryResult.type) {
    case QueryResultType.PENDING:
      return <CenteredHorizontalThrobber />;
    case QueryResultType.SUCCESS: {
      const selectedDashboard = props.selectedDashboardQueryResult.data.analyticsDashboard;
      if (isInaccessible(selectedDashboard)) {
        return (
          <>
            <div className="u-small-spacing-bottom">
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to the previously selected dashboard. Please select a new dashboard."
              />
            </div>
            <AnalyticsDashboardSelector selectedAnalyticsDashboard={null} {...sharedProps} />
          </>
        );
      } else {
        return <AnalyticsDashboardSelector selectedAnalyticsDashboard={selectedDashboard} {...sharedProps} />;
      }
    }
    case QueryResultType.ERROR:
      notifications.error('Unable to load the previously selected dashboard. Please try again.');
      return null;
    case QueryResultType.SKIPPED:
      return <AnalyticsDashboardSelector selectedAnalyticsDashboard={null} {...sharedProps} />;
    default:
      assertNever(props.selectedDashboardQueryResult);
      return null;
  }
}

function getIsAccessibleDashboardSelected(
  selectedDashboardQueryResult: SkippableBenchlingQueryResult<
    DashboardAndBlockSelectorFormGroupQuery,
    DashboardAndBlockSelectorFormGroupQueryVariables
  >
): boolean {
  return (
    selectedDashboardQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(selectedDashboardQueryResult.data.analyticsDashboard)
  );
}

function PlaceholderBlockSelector(): JSX.Element {
  return <SelectDropdown menu={<Menu />} disabled={true} displayName="" />;
}

export interface DashboardAndBlockSelectionState {
  /**
   * Selected dashboard
   */
  analyticsDashboardId: string | null;
  /**
   * Selected block
   */
  analyticsQueryId: string | null;
}

export interface ReadOnlyDashboardAndBlockSelectorFormGroupProps {
  dashboardName: string;
  blockName: string;
}

export type DashboardAndBlockSelectorFormGroupProps =
  | {
      /**
       * When false, then the dashboard and block field values are found by querying the dashboard using the
       * `selectionState` values.
       */
      readOnly: false;
      selectionState: DashboardAndBlockSelectionState;
      onSelect: (selectionState: DashboardAndBlockSelectionState) => void;
    }
  | ({
      /**
       * When true, then the dashboard and block field values are set statically using the `dashboardName` and
       * `blockName` values.
       */
      readOnly: true;
    } & ReadOnlyDashboardAndBlockSelectorFormGroupProps);

const EMPTY_SELECTION_STATE: DashboardAndBlockSelectionState = {
  analyticsDashboardId: null,
  analyticsQueryId: null,
};

/**
 * This component enables the user to select a block by (1) searching for a dashboard they have access to, and
 * then (2) selecting a block within that dashboard.
 */
export default function DashboardAndBlockSelectorFormGroup(
  props: DashboardAndBlockSelectorFormGroupProps
): JSX.Element | null {
  const {analyticsDashboardId, analyticsQueryId} = props.readOnly
    ? EMPTY_SELECTION_STATE
    : props.selectionState;
  // Query for the selected dashboard
  const queryResult = useSkippableBenchlingQuery<
    DashboardAndBlockSelectorFormGroupQuery,
    DashboardAndBlockSelectorFormGroupQueryVariables
  >(DASHBOARD_AND_BLOCK_SELECTOR_FORM_GROUP_QUERY, {
    // Skip the query if no selected dashboard
    skip: analyticsDashboardId == null,
    variables: {analyticsDashboardId: analyticsDashboardId!},
  });

  return props.readOnly ? (
    <DashboardAndBlockSelectorSkeleton
      dashboardSelector={
        <SelectDropdown
          menu={<SearchableMenu onSelect={noop} optionDatas={[]} width={Menu.WIDTHS.FULL} />}
          displayName={props.dashboardName}
          disabled={true}
        />
      }
      blockSelector={
        <SelectDropdown
          menu={<SearchableMenu onSelect={noop} optionDatas={[]} width={Menu.WIDTHS.FULL} />}
          displayName={props.blockName}
          disabled={true}
        />
      }
    />
  ) : (
    <DashboardAndBlockSelectorSkeleton
      dashboardSelector={
        <DashboardSelectorAdapter
          selectedDashboardId={analyticsDashboardId}
          selectedDashboardQueryResult={queryResult}
          onSelect={(newAnalyticsDashboardId: string) => {
            props.onSelect({
              analyticsDashboardId: newAnalyticsDashboardId,
              // When selecting a new dashboard, clear the previously selected block from the old dashboard
              analyticsQueryId: null,
            });
          }}
        />
      }
      blockSelector={
        analyticsDashboardId != null && getIsAccessibleDashboardSelected(queryResult) ? (
          <BlockInDashboardSelector
            parentDashboardId={analyticsDashboardId}
            selectedAnalyticsQueryId={analyticsQueryId}
            onSelect={(newAnalyticsQueryId: string) => {
              props.onSelect({analyticsDashboardId, analyticsQueryId: newAnalyticsQueryId});
            }}
          />
        ) : (
          <PlaceholderBlockSelector />
        )
      }
    />
  );
}
